import { Fragment, type JSX, jsx as localizedJsx, jsxs as localizedJsxs } from "@/lib/cash-jsx/jsx-runtime";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";
export { Fragment, type JSX };

function LocalizedPair({ english, arabic }: { english: string; arabic: string }) {
  const { i18n } = useTranslation();
  return i18n.language?.split("-")[0] === "ar" ? arabic : english;
}
function translatedPair(type: any, props: any) {
  if (!["label", "h2", "h3", "h4", "small", "button", "th", "p"].includes(type)) return props;
  const split = (value: unknown) => {
    if (typeof value !== "string") return value;
    const parts = /^([^/]+?)\s*\/\s*([\u0600-\u06ff][^/]*)$/.exec(value.trim());
    return parts ? localizedJsx(LocalizedPair, { english: parts[1].trim(), arabic: parts[2].trim() }) : value;
  };
  const value = props?.children;
  if (typeof value === "string") return { ...props, children: split(value) };
  if (Array.isArray(value)) return { ...props, children: value.map(split) };
  return props;
}

/* These views use the cash-only translation runtime. Adapt their existing
   semantic table/card/button markup to the real shared UI primitives without
   touching query, mutation, or handler code in any cash workflow. */
function shared(type: any, props: any): [any, any] {
  if (type === "table") return [Table, props];
  if (type === "thead") return [TableHeader, props];
  if (type === "tbody") return [TableBody, props];
  if (type === "tr") return [TableRow, props];
  if (type === "th") return [TableHead, props];
  if (type === "td") return [TableCell, props];
  if (type === "input" && !["checkbox", "radio", "file", "hidden"].includes(props?.type || "")) return [Input, props];
  if (type === "textarea") return [Textarea, props];
  if (type === "section" && /\bcb-card\b/.test(props?.className || "")) return [Card, props];
  if (type === "button" && /\bcb-btn\b/.test(props?.className || "")) {
    const className = props.className || "";
    return [Button, { ...props, variant: props.variant ?? (/\bprimary\b/.test(className) ? "default" : /\btext\b/.test(className) ? "ghost" : "outline") }];
  }
  if (type === "span" && (/\bcb-chip\b/.test(props?.className || "") || props?.children === "Never counted"))
    return [Badge, { ...props, variant: props.variant ?? "outline" }];
  return [type, props];
}
export function jsx(type: any, props: any, key?: any): JSX.Element {
  const [component, adapted] = shared(type, translatedPair(type, props));
  return localizedJsx(component, adapted, key);
}
export function jsxs(type: any, props: any, key?: any): JSX.Element {
  const [component, adapted] = shared(type, translatedPair(type, props));
  return localizedJsxs(component, adapted, key);
}